// src/components/music/parts/PlaylistManager.jsx

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Trash2, Music, Edit2, Check } from 'lucide-react';

export const PlaylistManager = ({
  playlists,
  selectedPlaylist,
  onSelectPlaylist,
  onCreatePlaylist,
  onDeletePlaylist,
  onAddTrack,
  onRemoveTrack,
  onUpdatePlaylist,
  currentTrack,
  isPlaying
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDesc, setNewPlaylistDesc] = useState('');
  const [editingPlaylistId, setEditingPlaylistId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;

    await onCreatePlaylist(newPlaylistName, newPlaylistDesc);
    setNewPlaylistName('');
    setNewPlaylistDesc('');
    setShowCreateForm(false);
  };

  const handleUpdatePlaylist = async (playlistId, newName) => {
    if (!newName.trim()) return;
    await onUpdatePlaylist(playlistId, { name: newName });
    setEditingPlaylistId(null);
  };

  const handleAddTrackToPlaylist = (playlistId) => {
    if (currentTrack) {
      onAddTrack(playlistId, currentTrack);
    }
  };

  return (
    <div className="fixed bottom-5 right-6 z-40">
    {/* Main Button */}
    <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white/10 backdrop-blur-xl border border-white/20 text-white p-3 rounded-full shadow-2xl transition transform hover:bg-white/20 hover:scale-110"
        title="Playlists"
    >
        <Music size={20} />
    </button>

      {/* Playlist Panel */}
     <AnimatePresence>
        {isOpen && (
        <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="absolute bottom-16 right-0 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-4 w-80 max-h-96 overflow-y-auto border border-white/20"
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-white/20">
              <h3 className="text-lg font-semibold text-white">My Playlists</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Create Playlist Form */}
            {showCreateForm ? (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handleCreatePlaylist}
                className="mb-4 p-3 bg-white/10 rounded-xl space-y-2 border border-white/20"
              >
                <input
                  type="text"
                  placeholder="Playlist name..."
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 text-white placeholder-white/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/60 text-sm border border-white/10"
                  autoFocus
                />
                <textarea
                  placeholder="Description (optional)"
                  value={newPlaylistDesc}
                  onChange={(e) => setNewPlaylistDesc(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 text-white placeholder-white/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/60 text-sm h-16 resize-none border border-white/10"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-3 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/40 rounded-lg transition text-sm font-medium"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="flex-1 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition text-sm font-medium border border-white/20"
                  >
                    Cancel
                  </button>
                </div>
              </motion.form>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full mb-4 px-3 py-2 bg-white/20 border border-white/20 text-white rounded-xl hover:bg-white/30 transition text-sm font-medium flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                New Playlist
              </button>
            )}

            {/* Playlists List */}
            {playlists.length === 0 ? (
              <p className="text-center text-white/60 text-sm py-4">No playlists yet</p>
            ) : (
              <div className="space-y-2">
                {playlists.map((playlist) => (
                  <motion.div
                    key={playlist.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-3 bg-white/10 rounded-xl border border-white/20 hover:border-white/30 transition"
                  >
                    {/* Playlist Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => onSelectPlaylist(playlist)}
                      >
                        {editingPlaylistId === playlist.id ? (
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="flex-1 px-2 py-1 bg-white/10 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-cyan-400/60 border border-white/10"
                              autoFocus
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdatePlaylist(playlist.id, editingName);
                              }}
                              className="p-1 text-cyan-300 hover:text-cyan-200"
                            >
                              <Check size={16} />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <h4
                              className={`text-sm font-semibold ${
                                selectedPlaylist?.id === playlist.id
                                  ? 'text-cyan-300'
                                  : 'text-white'
                              }`}
                            >
                              {playlist.name}
                            </h4>
                            <p className="text-xs text-white/60">
                              {playlist.tracks?.length || 0} track
                              {playlist.tracks?.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {editingPlaylistId !== playlist.id && (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPlaylistId(playlist.id);
                              setEditingName(playlist.name);
                            }}
                            className="p-1 text-white/60 hover:text-cyan-300 transition"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePlaylist(playlist.id);
                            }}
                            className="p-1 text-white/60 hover:text-red-400 transition"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Add Current Track Button */}
                    {currentTrack && (
                      <button
                        onClick={() => handleAddTrackToPlaylist(playlist.id)}
                        className="w-full px-2 py-1.5 bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/40 rounded text-xs font-medium transition"
                      >
                        + Add Current Track
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
